<script setup>
import { computed, reactive, watch, ref, nextTick } from "vue";
import VBaseInput from "../Inputs/VBaseInput.vue";
import VProductSelect from "../Inputs/VProductSelect.vue";
import VBaseSelectMenu from "../Inputs/VBaseSelectMenu.vue";
import { MinusIcon as MinusIconMini, PlusIcon as PlusIconMini } from '@heroicons/vue/20/solid'
import Datepicker from '@vuepic/vue-datepicker';
import { fr } from 'date-fns/locale';
import '@vuepic/vue-datepicker/dist/main.css'
import VBaseTextarea from "../Inputs/VBaseTextarea.vue";
import VButtonGenerateReminder from "./VButtonGenerateReminder.vue";
import VErrorBox from "./VErrorBox.vue";
import * as PRODUCT from "../composables/useProducts"
import useApp from "../composables/useApp";
import { useI18n } from "vue-i18n";
import { formStart } from "../composables/useTracking";

const { t } = useI18n();

//get language from dom
const language = ref(document.querySelector('html').getAttribute('lang'));
const timepicker = ref();
const formStarted = ref(false);

const { debug } = useApp();
/**
 * Main form state
 * @type {UnwrapNestedRefs<{
 * petName: string,
 * product: object,
 * specie: string,
 * format: object,
 * chews: object,
 * numberOfChews: string,
 * capsules: array,
 * dosage: string || number,
 * frequency: object,
 * date: Date,
 * specialComments: string,
 * timezone: string,
 * time: string,
 * howOften: string,
 * firstBottle: string
 * }>}
 */
const state = reactive({
    petName: "",
    product: null,
    specie: "dog",
    ageYears: null,
    ageMonths: null,
    format: null,
    chews: null,
    numberOfChews: "",
    senvelgoCatWeight: "",
    capsules: [{
        strength: "",
        dosage: ""
    }],
    dosage: "",
    frequency: null,
    date: null,
    vaccinationDates: [null, null, null, null],
    specialComments: "",
    timezone: (() => {
        const offset = -new Date().getTimezoneOffset() / 60;
        return Intl.DateTimeFormat().resolvedOptions().timeZone + " (GMT" + (offset > 0 ? "+" : "") + offset + ")";
    })(), //default to user timezone
    timezoneRaw: Intl.DateTimeFormat().resolvedOptions().timeZone,
    time: null,
    howOften: "",
    firstBottle: ""
});

const allRequiredFieldsPerProductFilled = ref(false);

const options = reactive({
    format: {
        [PRODUCT.METACAM]: {
            "dog": [
                {
                    id: "10ml",
                    name: "10mL"
                },
                {
                    id: "32ml",
                    name: "32mL"
                },
                {
                    id: "100ml",
                    name: "100mL"
                },
                {
                    id: "180ml",
                    name: "180mL"
                },
                {
                    id: "chews",
                    name: t('option.chews')
                }
            ],
        },
        [PRODUCT.SEMINTRA]: {
            "cat": [
                {
                    id: "30ml",
                    name: "30mL"
                }
            ],
        },
    },
    chews: {
        [PRODUCT.NEXGARD_SPECTRA]: {
            "dog": [
                {
                    id: "1",
                    name: "1"
                },
                {
                    id: "2",
                    name: "2"
                },
                {
                    id: "3",
                    name: "3"
                },
                {
                    id: "4",
                    name: "4"
                },
                {
                    id: "5",
                    name: "5"
                },
                {
                    id: "6",
                    name: "6"
                },
                {
                    id: "7",
                    name: "7"
                },
                {
                    id: "8",
                    name: "8"
                },
                {
                    id: "9",
                    name: "9"
                },
                {
                    id: "10",
                    name: "10"
                },
                {
                    id: "11",
                    name: "11"
                },
                {
                    id: "12",
                    name: "12"
                },
            ],
        },
        [PRODUCT.NEXGARD]: {
            "dog": [
                {
                    id: "1",
                    name: "1"
                },
                {
                    id: "2",
                    name: "2"
                },
                {
                    id: "3",
                    name: "3"
                },
                {
                    id: "4",
                    name: "4"
                },
                {
                    id: "5",
                    name: "5"
                },
                {
                    id: "6",
                    name: "6"
                },
                {
                    id: "7",
                    name: "7"
                },
                {
                    id: "8",
                    name: "8"
                },
                {
                    id: "9",
                    name: "9"
                },
                {
                    id: "10",
                    name: "10"
                },
                {
                    id: "11",
                    name: "11"
                },
                {
                    id: "12",
                    name: "12"
                },
            ],
        },
        [PRODUCT.HEARTGARD]: {
            "dog": [
                {
                    id: "1",
                    name: "1"
                },
                {
                    id: "2",
                    name: "2"
                },
                {
                    id: "3",
                    name: "3"
                },
                {
                    id: "4",
                    name: "4"
                },
                {
                    id: "5",
                    name: "5"
                },
                {
                    id: "6",
                    name: "6"
                },
                {
                    id: "7",
                    name: "7"
                },
                {
                    id: "8",
                    name: "8"
                },
                {
                    id: "9",
                    name: "9"
                },
                {
                    id: "10",
                    name: "10"
                },
                {
                    id: "11",
                    name: "11"
                },
                {
                    id: "12",
                    name: "12"
                },
            ],
        },
        [PRODUCT.NEXGARD_COMBO]: {
            "cat": [
                {
                    id: "1",
                    name: "1"
                },
                {
                    id: "2",
                    name: "2"
                },
                {
                    id: "3",
                    name: "3"
                },
                {
                    id: "4",
                    name: "4"
                },
                {
                    id: "5",
                    name: "5"
                },
                {
                    id: "6",
                    name: "6"
                },
                {
                    id: "7",
                    name: "7"
                },
                {
                    id: "8",
                    name: "8"
                },
                {
                    id: "9",
                    name: "9"
                },
                {
                    id: "10",
                    name: "10"
                },
                {
                    id: "11",
                    name: "11"
                },
                {
                    id: "12",
                    name: "12"
                },
            ],
        },
    },
    capsulesWeight: {
        en: [
            {
                id: "1.25 mg",
                name: "1.25 mg"
            },
            {
                id: "5 mg",
                name: "5 mg"
            },
        ],
        ms: [
            {
                id: "1.25 mg",
                name: "1.25 mg"
            },
            {
                id: "5 mg",
                name: "5 mg"
            },
        ],
    },
    frequency: [
        {
            id: "once",
            name: "Once",
            value: 1,
        },
        {
            id: "twice",
            name: "Twice",
            value: 2,
        }
    ],
    howOften: [
        {
            id: "everyday",
            name: t("option.everyday")
        },
        {
            id: "everyday-for-the-first-week",
            name: t("option.everyday.first.week")
        },
        {
            id: "just-the-refill-reminder",
            name: t("option.refill.reminder")
        }
    ],
    firstBottle: [
        {
            id: "yes",
            name: t("option.yes")
        },
        {
            id: "no",
            name: t("option.no")
        }
    ],
    ageYears: Array.from({ length: 31 }, (_, index) => ({
        id: String(index),
        name: String(index),
    })),
    ageMonths: Array.from({ length: 12 }, (_, index) => ({
        id: String(index),
        name: String(index),
    })),
})

const { useSimpleBanner } = useApp();

const requiredFields = {
    [PRODUCT.NEXGARD_COMBO]: ["date", "time"],
    [PRODUCT.FRONTLINE_PLUS]: ["date", "time",],
    [PRODUCT.PUREVAX]: ["date", "time"],
    [PRODUCT.SEMINTRA]: ["dosage", "date", "time", "howOften"],
    [PRODUCT.NEXGARD]: ["date", "time"],
    [PRODUCT.NEXGARD_SPECTRA]: ["date", "time"],
    [PRODUCT.RECOMBITEK]: ["ageYears", "ageMonths", "vaccinationDates", "time"],
    [PRODUCT.HEARTGARD]: ["date", "time"],
    [PRODUCT.SENVELGO]: ["senvelgoCatWeight", "date", "time", "howOften"],
}

const errors = ref([])
const loading = ref(false)

/**
 * Return true if both petsName and product are not empty.
 * @type {ComputedRef<unknown>}
 */
const showSecondStepForm = computed(() => {
    bannerModification();

    return state.petName && state.product;
});

const mainForm = ref(null)

const closeAndFocusTimePicker = () => {
    if (timepicker) {
        timepicker.value.closeMenu();
    }
}

const bannerModification = async () => {
    if (state.petName && state.product) {
        //show simple banner
        useSimpleBanner.value = true;

        if (document.activeElement.id !== "petName") {
            await nextTick();

            setTimeout(() => {
                mainForm.value.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });
            }, 200);
        }
    } else {
        //show full banner
        useSimpleBanner.value = false;
    }
}

const addCapsule = () => {
    if (state.capsules.length < 6) {
        state.capsules.push({ strength: "", dosage: "" });
    }
}

//watch reset values when specie is changes
watch(() => state.specie, () => {
    state.format = null;
});

//automatically set value to species when some product is selected
watch(() => state.product, (product) => {
    if (!state.product) return;
});

watch(() => state.product?.slug, (productSlug) => {
    if (productSlug === PRODUCT.RECOMBITEK) {
        state.date = null;
        return;
    }

    state.ageYears = null;
    state.ageMonths = null;
    state.vaccinationDates = [null, null, null, null];
});

watch(() => state, () => {
    if (formStarted.value === false) {
        formStart();
        formStarted.value = true;
    }

    if (!state.product?.slug) return;

    const requiredFieldsForProduct = requiredFields[state.product.slug];

    if (!requiredFieldsForProduct) return;

    const _errors = requiredFieldsForProduct.map((field) => {
        if (typeof field === "string") {
            if (!state[field]) {
                return field;
            } else {
                if (Array.isArray(state[field])) {
                    if (state[field].length === 0) {
                        return field;
                    } else if (field === "vaccinationDates") {
                        // Only first two dates are required
                        if (state[field][0] === null || state[field][1] === null) {
                            return field;
                        }
                    } else if (state[field].includes(null)) {
                        return field;
                    } else if (typeof state[field][0] === "object") {
                        for (let i = 0; i < state[field].length; i++) {
                            const obj = state[field][i];

                            if (Object.values(obj).includes(null) || Object.values(obj).includes("")) {
                                return field;
                            }
                        }
                    }
                }
            }
        }

        if (typeof field === "object") {
            const key = Object.keys(field[Object.keys(field)[0]])[0];
            const value = field[Object.keys(field)[0]][key];

            if (state[key]?.id === value) {
                if (!state[Object.keys(field)[0]]) {
                    return Object.keys(field)[0];
                }
            }
        }
    }).filter((error) => error);

    errors.value = _errors;

    allRequiredFieldsPerProductFilled.value = _errors.length === 0;

}, { deep: true });
</script>

<template>
    <div>
        <section class="-mx-8 px-3 my-8">
            <!-- Blue section form -->
            <div class="bg-bi-dark-green py-8 px-5 grid grid-cols-1 gap-8">
                <v-product-select v-model="state.product" v-model:petName="state.petName" v-model:petType="state.specie"></v-product-select>

                <v-error-box v-if="errors.length && debug" :errors="errors"></v-error-box>
            </div>
        </section>

        <section class="-mx-8 px-3 my-8" v-auto-animate ref="mainForm">
            <!-- White section -->
            <div class="bg-bi-light-gray py-8 px-5 grid grid-cols-1 gap-8" v-if="showSecondStepForm" v-auto-animate>

                <!-- CAT WEIGHT -->
                <!-- Display only for Senvelgo and cat -->
                <v-base-input id="senvelgoCatWeight" :label="$t('form.senvelgoCatWeight')" :key="'senvelgoCatWeight'"
                              type="number" theme="light" :placeholder="$t('form.senvelgoCatWeightPlaceholder')"
                              v-if="state.product?.slug === PRODUCT.SENVELGO"
                              v-model.number="state.senvelgoCatWeight"></v-base-input>

                <!-- CAPSULES -->

                <!-- DOSAGE -->
                <!-- Display only for Semintra format Chews -->
                <v-base-input id="dosage" :key="'dosage'"
                    :label="state.product?.slug === PRODUCT.SEMINTRA ? $t('form.cat.weight') : $t('form.dog.weight')"
                    theme="light" type="number" step="0.01"
                    :placeholder="state.format?.id === 'chews' ? $t('form.dosage.placeholder.chews') : $t('form.dosage.placeholder')"
                    v-if="state.product?.slug === PRODUCT.SEMINTRA"
                    v-model="state.dosage"></v-base-input>

                <div v-if="state.product?.slug === PRODUCT.RECOMBITEK" class="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <v-base-select-menu id="age-years" :label="$t('form.ageYears')" :options="options.ageYears"
                        v-model="state.ageYears"></v-base-select-menu>

                    <v-base-select-menu id="age-months" :label="$t('form.ageMonths')" :options="options.ageMonths"
                        v-model="state.ageMonths"></v-base-select-menu>
                </div>

                <!-- REMINDER START DATE -->
                <div v-if="state.product?.slug !== PRODUCT.RECOMBITEK">
                    <p v-if="state.product?.slug !== PRODUCT.RECOMBITEK && state.product?.slug !== PRODUCT.PUREVAX" class="text-base font-medium text-text-300 mb-1">{{ $t('form.reminderStartDate') }}</p>
                    <p v-else class="text-base font-medium text-text-300 mb-1">{{ $t('form.vaccinationDate') }}</p>
                    <datepicker v-model="state.date" auto-apply :locale="$i18n.locale" format="yyyy-MM-dd" :day-names="['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']"
                        :enableTimePicker="false" :transitions="false"
                        :placeholder="$t('form.reminderStartDate.placeholder')"></datepicker>
                </div>

                <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div v-for="(_, index) in state.vaccinationDates" :key="`vaccination-date-${index}`">
                        <p class="text-base font-medium text-text-300 mb-1">
                            {{ $t(`form.reminderStartDate${index + 1}.placeholder`) }}
                        </p>
                        <datepicker v-model="state.vaccinationDates[index]" auto-apply :locale="$i18n.locale" :day-names="['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']" format="yyyy-MM-dd"
                            :enableTimePicker="false" :transitions="false"
                            :placeholder="$t('form.vaccinationDateNumber')"></datepicker>
                    </div>
                </div>

                <div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div>
                            <p class="text-base font-medium text-text-300 mb-1">{{ $t('form.reminderStartTime') }}</p>

                            <datepicker v-model="state.time" ref="timepicker" time-picker :is-24="false" :day-names="['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']"
                                :enableTimePicker="true" :transitions="false"
                                :placeholder="$t('form.reminderStartTime.placeholder')">
                                <template #am-pm-button="{ toggle, value }">
                                    <button type="button" class="dp__pm_am_button" @click="toggle">
                                        {{ value === 'AM' ? 'SA' : 'CH' }}
                                    </button>
                                </template>
                                <template #action-row="{ internalModelValue, selectDate }">
                                    <div class="flex justify-end w-full">
                                        <button class="select-button font-bold" @click="selectDate">{{
                                            $t('form.reminderStartTime.button') }}</button>
                                    </div>
                                </template>
                            </datepicker>
                        </div>

                        <div>
                            <v-base-input :key="'timezone'" id="timezone" :label="$t('select.timezone')"
                                v-model="state.timezone" disabled></v-base-input>
                        </div>
                    </div>
                </div>

                <!-- How often the user wants a reminder - Semintra, Senvelgo -->
                <v-base-select-menu id="how-often" :label="$t('how.often')" :options="options.howOften"
                    v-model="state.howOften"
                    v-if="state.product?.slug === PRODUCT.SEMINTRA || state.product?.slug === PRODUCT.SENVELGO">
                </v-base-select-menu>

                <!-- SPECIAL COMMENTS -->
                <v-base-textarea id="specialComments" name="special-comments" v-model="state.specialComments"
                    :label="$t('form.specialComments')"></v-base-textarea>

                <!-- GENERATE -->
                <div class="flex justify-center flex-wrap">
                    <v-button-generate-reminder :disabled="!allRequiredFieldsPerProductFilled"
                        :data="state"></v-button-generate-reminder>
                </div>
            </div>
        </section>
    </div>
</template>

<style lang="scss">
.dp__main,
.dp__calendar_wrap {
    font-family: 'Inter', sans-serif;
}

.dp__theme_light {
    --dp-primary-color: #08312A;
    --dp-text-color: #08312A;
    --dp-icon-color: #E5E3DE;
}

.dp__input_icon {
    right: 0;
    left: auto;
    @apply text-bi-warm-gray;
}

.dp__clear_icon {
    right: 30px;
}

.dp__input_icon_pad {
    padding-left: 0.75rem;
}

.dp__input {
    @apply border-bi-warm-gray border-2 font-bi-text-medium focus:border-bi-accent-green;
    padding: 0.75rem 2.5rem 0.75rem 0.75rem;
    @apply rounded-md pr-10 py-3 text-base text-bi-dark-green font-semibold placeholder-bi-gray-60;

    &:hover {
        @apply border-bi-warm-gray;
    }
}

.dp__input::placeholder {
    @apply placeholder-bi-gray-60 font-bi-text;
}

.dp__active_date {
    @apply bg-white text-bi-dark-green font-bi-text-bold;
}

.dp__input_focus {
    @apply ring-0 border-bi-accent-green;
}

.dp__cell_inner,
.dp__calendar_header_item {
    width: 40px;
    height: 40px;
}

.dp__menu {
    @apply shadow-2xl border-0 p-4;
    border-radius: 6px
}

.dp__pm_am_button {
    &:hover {
        @apply bg-bi-dark-green text-white;
    }
}
</style>
